// ============================================================================
// App shell — header and footer.
//
// Rendered from JS rather than sitting static in index.html because the nav
// has to change based on who is signed in: a visitor sees "Apply", a creator
// sees their portal, an admin sees the control board.
// ============================================================================

import { html } from '../lib/html.js';
import { getSession, getProfile, isAdmin, signOut } from '../lib/auth.js';
import { initials } from '../lib/format.js';
import { brandLogo, PARTNER_BRANDS } from '../lib/media.js';
import { trackImageLoads } from '../lib/motion.js';

export async function renderHeader() {
  const session = await getSession();
  const [profile, admin] = session
    ? await Promise.all([getProfile(), isAdmin()])
    : [null, false];

  const signedIn = Boolean(session);
  const approved = profile?.status === 'approved';

  return html`
    <header class="dw-header">
      <div class="dw-container dw-header-inner">
        <a href="#/" class="dw-logo-group" aria-label="Detailing World Squad home">
          ${brandLogo()}
        </a>

        <button class="dw-nav-toggle" id="dw-nav-toggle"
                aria-expanded="false" aria-controls="dw-nav-links" aria-label="Menu">
          <span></span><span></span><span></span>
        </button>

        <nav class="dw-nav-links" id="dw-nav-links">
          ${approved ? html`
            <a href="#/dashboard" class="dw-nav-link" data-nav="/dashboard">Dashboard</a>
            <a href="#/missions"  class="dw-nav-link" data-nav="/missions">Missions</a>
            <a href="#/drops"     class="dw-nav-link" data-nav="/drops">Drops</a>
            <a href="#/rewards"   class="dw-nav-link" data-nav="/rewards">Rewards</a>
          ` : html`
            <a href="#/" class="dw-nav-link" data-nav="/">The Program</a>
          `}

          ${admin ? html`
            <a href="#/admin" class="dw-nav-link dw-nav-link--admin" data-nav="/admin">Control Board</a>
          ` : ''}

          ${signedIn ? html`
            <div class="dw-user-menu">
              <button class="dw-avatar" id="dw-user-btn" aria-haspopup="true" aria-expanded="false"
                      title="${profile?.full_name ?? 'Account'}">
                ${initials(profile?.full_name ?? 'DW')}
              </button>
              <div class="dw-user-dropdown" id="dw-user-dropdown" hidden>
                <div class="dw-user-dropdown-head">
                  <strong>${profile?.full_name ?? 'Signed in'}</strong>
                  <span class="dw-meta">${profile?.email ?? ''}</span>
                </div>
                ${approved ? html`<a href="#/profile" class="dw-dropdown-item">Profile</a>` : ''}
                <button class="dw-dropdown-item dw-dropdown-item--danger" id="dw-signout">Sign out</button>
              </div>
            </div>
          ` : html`
            <a href="#/login" class="dw-nav-link">Sign in</a>
            <a href="#/apply" class="dw-btn dw-btn--primary dw-btn--sm">Apply Now</a>
          `}
        </nav>
      </div>
    </header>
  `;
}

/**
 * Footer. Separate from the header because they mount into different points
 * in document order — rendering both into one container above <main> is what
 * put the footer at the top of the page.
 */
export async function renderFooter() {
  return html`
    <footer class="dw-footer">
      <div class="dw-container dw-footer-grid">
        <div>
          ${brandLogo()}
          <p class="dw-small dw-muted">
            The creator program for Detailing World. Real codes, real numbers,
            store credit that actually adds up.
          </p>
        </div>
        <div>
          <span class="dw-footer-title">Program</span>
          <a href="#/" class="dw-footer-link">How it works</a>
          <a href="#/apply" class="dw-footer-link">Apply</a>
          <a href="#/login" class="dw-footer-link">Sign in</a>
        </div>
        <div>
          <span class="dw-footer-title">Brands we carry</span>
          <p class="dw-small dw-muted">
            ${PARTNER_BRANDS.map((b) => b.name).join(' · ')}
          </p>
        </div>
      </div>
      <div class="dw-container dw-footer-legal">
        <span class="dw-meta dw-muted">
          © ${new Date().getFullYear()} Detailing World.
        </span>
      </div>
    </footer>
  `;
}

export function mountHeader() {
  // The header re-renders on sign in and out; its logo needs the load
  // listener re-attached each time or it stays faded out.
  trackImageLoads(document.getElementById('shell-header') ?? document);

  // refreshShell after signOut, or the header keeps showing the avatar and
  // creator nav of the session that just ended — which reads as a failed
  // sign-out even though the tokens are gone.
  document.getElementById('dw-signout')?.addEventListener('click', async () => {
    await signOut();
    await refreshShell();
  });

  const navToggle = document.getElementById('dw-nav-toggle');
  const navLinks = document.getElementById('dw-nav-links');
  navToggle?.addEventListener('click', () => {
    const open = navLinks.classList.toggle('is-open');
    navToggle.setAttribute('aria-expanded', String(open));
  });

  // Close the mobile nav after navigating, or it covers the page you just
  // asked for.
  navLinks?.addEventListener('click', (e) => {
    if (e.target.closest('a')) {
      navLinks.classList.remove('is-open');
      navToggle?.setAttribute('aria-expanded', 'false');
    }
  });

  const userBtn = document.getElementById('dw-user-btn');
  const dropdown = document.getElementById('dw-user-dropdown');
  userBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    const open = dropdown.hasAttribute('hidden');
    dropdown.toggleAttribute('hidden', !open);
    userBtn.setAttribute('aria-expanded', String(open));
  });
  document.addEventListener('click', () => {
    if (dropdown && !dropdown.hasAttribute('hidden')) {
      dropdown.setAttribute('hidden', '');
      userBtn?.setAttribute('aria-expanded', 'false');
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      dropdown?.setAttribute('hidden', '');
      navLinks?.classList.remove('is-open');
    }
  });
}

/** Mount both shell regions. Called once at boot. */
export async function mountShellRegions() {
  const header = document.getElementById('shell-header');
  const footer = document.getElementById('shell-footer');

  if (header) {
    header.innerHTML = String(await renderHeader());
    mountHeader();
  }
  // The footer has no auth-dependent content, so it only needs rendering once.
  if (footer && !footer.childElementCount) {
    footer.innerHTML = String(await renderFooter());
  }
}

/** Re-render the header after sign in/out or a profile change. */
export async function refreshShell() {
  const header = document.getElementById('shell-header');
  if (!header) return;
  header.innerHTML = String(await renderHeader());
  mountHeader();
}

export function setActiveNav(path) {
  document.querySelectorAll('#dw-nav-links .dw-nav-link').forEach((link) => {
    const target = link.getAttribute('data-nav');
    const active = target === path || (target !== '/' && path.startsWith(target));
    link.classList.toggle('active', Boolean(target) && active);
  });
}
